import { LitElement, html, css, nothing } from 'lit';
import { generationChanged } from '../utils/constants.js';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import type { ModelSnapshot, ModelDiff, ElementDiff, DiffCategory } from '@arctek/sysml-core';
import { formatDiffSummary } from '@arctek/sysml-core';

// ---------------------------------------------------------------------------
// <sysml-diff-history-panel> -- Semantic model history with diff viewer
//
// Displays model snapshots with semantic diff information.
// Shows: element adds/removes/modifications per snapshot, diff summaries,
// category breakdowns, and click-to-reveal navigation.
// ---------------------------------------------------------------------------

@customElement('sysml-diff-history-panel')
export class SysmlDiffHistoryPanel extends LitElement {
  static override styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
      }
      .history-header {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
      }
      .history-title {
        font-size: var(--r5-text-sm);
        font-weight: 600;
        flex: 1;
      }
      .snap-count {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
      }
      .action-row {
        display: flex;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
      }
      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--r5-space-1);
        padding: var(--r5-space-1) var(--r5-space-2);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        background: transparent;
        color: var(--r5-text);
        font-size: var(--r5-font-size-badge);
        cursor: pointer;
        transition: background var(--r5-duration-fast) var(--r5-ease-default);
      }
      .action-btn:hover {
        background: color-mix(in srgb, var(--r5-text) 5%, transparent);
      }
      .snapshot-list {
        flex: 1;
        overflow-y: auto;
        padding: var(--r5-space-2);
      }
      .snapshot-item {
        padding: var(--r5-space-2);
        border-radius: var(--r5-radius-md);
        margin-bottom: var(--r5-space-1);
        cursor: pointer;
        transition: background var(--r5-duration-fast) var(--r5-ease-default);
      }
      .snapshot-item:hover {
        background: color-mix(in srgb, var(--r5-text) 5%, transparent);
      }
      .snapshot-item.selected {
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
      }
      .snap-row {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
      }
      .snap-time {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
      }
      .snap-label {
        font-size: var(--r5-text-sm);
        font-weight: 500;
      }
      .snap-elements {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        margin-left: auto;
      }
      .diff-summary {
        display: flex;
        gap: var(--r5-space-1);
        margin-top: var(--r5-space-1);
        flex-wrap: wrap;
      }
      .diff-chip {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        padding: 1px 6px;
        border-radius: var(--r5-radius-sm);
        font-size: var(--r5-font-size-badge);
        font-weight: 500;
      }
      .diff-chip.added {
        background: color-mix(in srgb, var(--r5-success-500, #22c55e) 15%, transparent);
        color: var(--r5-success-700, #15803d);
      }
      .diff-chip.removed {
        background: color-mix(in srgb, var(--r5-error-500, #ef4444) 15%, transparent);
        color: var(--r5-error-700, #b91c1c);
      }
      .diff-chip.modified {
        background: color-mix(in srgb, var(--r5-warning-500, #f59e0b) 15%, transparent);
        color: var(--r5-warning-700, #a16207);
      }
      .diff-detail {
        margin-top: var(--r5-space-2);
        border-top: 1px solid var(--r5-border);
        padding-top: var(--r5-space-2);
      }
      .diff-element {
        display: flex;
        align-items: flex-start;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) 0;
        font-size: var(--r5-font-size-badge);
        cursor: pointer;
      }
      .diff-element:hover {
        color: var(--r5-primary);
      }
      .diff-op {
        font-weight: 700;
        font-family: var(--r5-font-mono);
        width: 14px;
        text-align: center;
        flex-shrink: 0;
      }
      .diff-op.added { color: var(--r5-success-500, #22c55e); }
      .diff-op.removed { color: var(--r5-error-500, #ef4444); }
      .diff-op.modified { color: var(--r5-warning-500, #f59e0b); }
      .diff-el-type {
        color: var(--r5-text-muted);
        font-family: var(--r5-font-mono);
        flex-shrink: 0;
      }
      .diff-el-name {
        font-weight: 500;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .diff-changes {
        margin-left: 22px;
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
      }
      .diff-change-line {
        font-family: var(--r5-font-mono);
        padding: 1px 0;
      }
      .category-filter {
        display: flex;
        gap: var(--r5-space-1);
        flex-wrap: wrap;
        padding: var(--r5-space-1) 0;
      }
      .cat-btn {
        padding: 1px 6px;
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-sm);
        background: transparent;
        color: var(--r5-text-muted);
        font-size: var(--r5-font-size-badge);
        cursor: pointer;
      }
      .cat-btn.active {
        background: color-mix(in srgb, var(--r5-primary) 15%, transparent);
        color: var(--r5-primary);
        border-color: var(--r5-primary);
      }
      .restore-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--r5-space-1);
        padding: var(--r5-space-1) var(--r5-space-2);
        border: 1px solid var(--r5-primary);
        border-radius: var(--r5-radius-md);
        background: transparent;
        color: var(--r5-primary);
        font-size: var(--r5-font-size-badge);
        cursor: pointer;
        margin-top: var(--r5-space-2);
      }
      .restore-btn:hover {
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
      }
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-6);
        color: var(--r5-text-muted);
        text-align: center;
      }
      .empty-state p {
        margin: 0;
        font-size: var(--r5-text-sm);
      }
    `,
  ];

  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;

  @state() private _selectedIndex: number | null = null;
  @state() private _categoryFilter: DiffCategory | null = null;

  override render() {
    const snaps = this.modeler.modelSnapshots;

    if (snaps.length === 0) {
      return this._renderEmpty();
    }

    return html`
      ${this._renderHeader(snaps)}
      ${this._renderActions()}
      <div class="snapshot-list">
        ${snaps.map((snap, i) => this._renderSnapshot(snap, i))}
      </div>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="empty-state">
        <p>No model snapshots yet</p>
        <p style="font-size: var(--r5-font-size-badge); color: var(--r5-text-muted);">
          Snapshots are created automatically as you edit. They capture the
          parsed model and compute semantic diffs.
        </p>
      </div>
    `;
  }

  private _renderHeader(snaps: ModelSnapshot[]) {
    return html`
      <div class="history-header">
        <span class="history-title">Semantic History</span>
        <span class="snap-count">${snaps.length} snapshot${snaps.length !== 1 ? 's' : ''}</span>
      </div>
    `;
  }

  private _renderActions() {
    return html`
      <div class="action-row">
        <button class="action-btn" @click=${this._onCheckpoint}>
          Checkpoint
        </button>
        <button class="action-btn" @click=${this._onClear}>
          Clear
        </button>
      </div>
    `;
  }

  private _renderSnapshot(snap: ModelSnapshot, index: number) {
    const isSelected = this._selectedIndex === index;
    const diff = snap.diffFromPrevious;

    return html`
      <div
        class="snapshot-item ${isSelected ? 'selected' : ''}"
        @click=${() => this._selectSnapshot(index)}
      >
        <div class="snap-row">
          <span class="snap-time">${this._formatTime(snap.timestamp)}</span>
          ${snap.label ? html`<span class="snap-label">${snap.label}</span>` : nothing}
          <span class="snap-elements">${snap.elementCount} elements</span>
        </div>

        ${diff ? this._renderDiffSummary(diff) : index === this.modeler.modelSnapshots.length - 1
          ? html`<div class="diff-summary"><span class="diff-chip modified">Initial snapshot</span></div>`
          : nothing}

        ${isSelected ? this._renderDiffDetail(snap, index) : nothing}
      </div>
    `;
  }

  private _renderDiffSummary(diff: ModelDiff) {
    const s = diff.summary;
    if (s.total === 0) {
      return html`<div class="diff-summary"><span class="diff-chip modified">No semantic changes</span></div>`;
    }

    return html`
      <div class="diff-summary">
        ${s.added > 0 ? html`<span class="diff-chip added">+${s.added} added</span>` : nothing}
        ${s.removed > 0 ? html`<span class="diff-chip removed">-${s.removed} removed</span>` : nothing}
        ${s.modified > 0 ? html`<span class="diff-chip modified">~${s.modified} modified</span>` : nothing}
      </div>
    `;
  }

  private _renderDiffDetail(snap: ModelSnapshot, index: number) {
    const diff = snap.diffFromPrevious;
    if (!diff || diff.diffs.length === 0) {
      return html`
        <div class="diff-detail">
          <p style="font-size: var(--r5-font-size-badge); color: var(--r5-text-muted); margin: 0;">
            ${index === this.modeler.modelSnapshots.length - 1 ? 'First snapshot — no previous version to compare.' : 'No element-level changes detected.'}
          </p>
          <button class="restore-btn" @click=${(e: Event) => { e.stopPropagation(); this._onRestore(index); }}>
            Restore this version
          </button>
        </div>
      `;
    }

    // Filter by category if active
    const filteredDiffs = this._categoryFilter
      ? diff.diffs.filter(d => d.categories.includes(this._categoryFilter!))
      : diff.diffs;

    // Collect active categories for filter buttons
    const activeCats = new Set<DiffCategory>();
    for (const d of diff.diffs) {
      for (const c of d.categories) activeCats.add(c);
    }

    return html`
      <div class="diff-detail">
        ${activeCats.size > 1 ? html`
          <div class="category-filter">
            <button
              class="cat-btn ${this._categoryFilter === null ? 'active' : ''}"
              @click=${(e: Event) => { e.stopPropagation(); this._setCategoryFilter(null); }}
            >All</button>
            ${[...activeCats].map(cat => html`
              <button
                class="cat-btn ${this._categoryFilter === cat ? 'active' : ''}"
                @click=${(e: Event) => { e.stopPropagation(); this._setCategoryFilter(cat); }}
              >${cat}</button>
            `)}
          </div>
        ` : nothing}

        ${filteredDiffs.slice(0, 20).map(d => this._renderElementDiff(d))}
        ${filteredDiffs.length > 20 ? html`
          <div style="font-size: var(--r5-font-size-badge); color: var(--r5-text-muted); padding: var(--r5-space-1) 0;">
            ... and ${filteredDiffs.length - 20} more changes
          </div>
        ` : nothing}

        <button class="restore-btn" @click=${(e: Event) => { e.stopPropagation(); this._onRestore(index); }}>
          Restore this version
        </button>
      </div>
    `;
  }

  private _renderElementDiff(d: ElementDiff) {
    const opSymbol = d.operation === 'added' ? '+' : d.operation === 'removed' ? '-' : '~';

    return html`
      <div class="diff-element" @click=${(e: Event) => { e.stopPropagation(); this._onElementClick(d); }}>
        <span class="diff-op ${d.operation}">${opSymbol}</span>
        <span class="diff-el-type">${d.elementType}</span>
        <span class="diff-el-name">${d.name}</span>
      </div>
      ${d.operation === 'modified' && d.changes ? html`
        <div class="diff-changes">
          ${d.changes.slice(0, 3).map(c => html`
            <div class="diff-change-line">${c.path}: ${this._formatVal(c.oldValue)} -> ${this._formatVal(c.newValue)}</div>
          `)}
          ${d.changes.length > 3 ? html`<div class="diff-change-line">... +${d.changes.length - 3} more</div>` : nothing}
        </div>
      ` : nothing}
    `;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  private _formatTime(timestamp: number): string {
    const diffMs = Date.now() - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;

    return new Date(timestamp).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private _formatVal(v: unknown): string {
    if (v === undefined) return 'undefined';
    if (v === null) return 'null';
    if (typeof v === 'string') return v.length > 20 ? `"${v.slice(0, 20)}..."` : `"${v}"`;
    if (Array.isArray(v)) return `[${v.length}]`;
    if (typeof v === 'object') return '{...}';
    return String(v);
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  private _selectSnapshot(index: number): void {
    this._selectedIndex = this._selectedIndex === index ? null : index;
    this._categoryFilter = null;
  }

  private _setCategoryFilter(cat: DiffCategory | null): void {
    this._categoryFilter = cat;
  }

  private _onCheckpoint(): void {
    this.modeler.createCheckpoint('Manual checkpoint');
  }

  private _onClear(): void {
    this.modeler.clearSnapshots();
    this._selectedIndex = null;
  }

  private _onRestore(index: number): void {
    this.modeler.restoreSnapshot(index);
    this._selectedIndex = null;
  }

  private _onElementClick(d: ElementDiff): void {
    // Find the element in the current model and reveal its source
    const el = this.modeler.allElements.find(
      e => e.qualifiedName === d.qualifiedName,
    );
    if (el?.source) {
      this.modeler.revealInEditor(el.source);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-diff-history-panel': SysmlDiffHistoryPanel;
  }
}
